// src/pages/Trains.js
import { useEffect, useState, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { rtdb } from '../firebase/config';
import { PageHeader, Card, SearchBar } from '../components/UI';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const trainIcon = (speed) => L.divIcon({
  html: `<div style="
    background: ${speed > 10 ? '#00a32a' : '#dba617'};
    border: 2px solid #fff;
    border-radius: 50%;
    width: 14px; height: 14px;
    box-shadow: 0 0 8px ${speed > 10 ? '#00a32a' : '#dba617'};
  "></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  className: '',
});

export default function Trains() {
  const [trains, setTrains] = useState({});
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});

  const today = new Date().toISOString().split('T')[0];

  // Init map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const map = L.map(mapRef.current, {
      center: [23.8103, 90.4125],
      zoom: 7,
      zoomControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);
    mapInstance.current = map;
  }, []);

  // Firebase listener
  useEffect(() => {
    const unsub = onValue(ref(rtdb, 'trains'), snap => {
      if (!snap.exists()) { setLoading(false); return; }
      const now = Date.now();
      const result = {};

      snap.forEach(trainSnap => {
        const trainId = trainSnap.key;
        const dayNode = trainSnap.child(`locations/${today}`);
        if (!dayNode.exists()) return;

        const activeLocs = [];
        dayNode.forEach(staffSnap => {
          const d = staffSnap.val();
          if ((now - (d.timestamp || 0)) < 15 * 60 * 1000 && !d.isOffline) {
            activeLocs.push(d);
          }
        });

        if (activeLocs.length === 0) return;

        const avgLat = activeLocs.reduce((s, l) => s + l.lat, 0) / activeLocs.length;
        const avgLng = activeLocs.reduce((s, l) => s + l.lng, 0) / activeLocs.length;
        const avgSpeed = activeLocs.reduce((s, l) => s + l.speed, 0) / activeLocs.length;
        const latest = activeLocs.reduce((a, b) => a.timestamp > b.timestamp ? a : b);

        result[trainId] = {
          trainId, lat: avgLat, lng: avgLng,
          speed: avgSpeed, timestamp: latest.timestamp,
          staffCount: activeLocs.length,
          staffName: latest.staffName,
        };
      });

      setTrains(result);
      setLoading(false);

      // Update markers
      const map = mapInstance.current;
      if (!map) return;

      Object.keys(markersRef.current).forEach(id => {
        if (!result[id]) { map.removeLayer(markersRef.current[id]); delete markersRef.current[id]; }
      });

      Object.entries(result).forEach(([id, t]) => {
        if (markersRef.current[id]) {
          markersRef.current[id].setLatLng([t.lat, t.lng]);
          markersRef.current[id].setIcon(trainIcon(t.speed));
        } else {
          const m = L.marker([t.lat, t.lng], { icon: trainIcon(t.speed) })
            .addTo(map)
            .bindPopup(`<b>Train #${id}</b><br>Speed: ${t.speed.toFixed(1)} km/h<br>Staff: ${t.staffCount} জন`);
          m.on('click', () => setSelected(t));
          markersRef.current[id] = m;
        }
      });
    });
    return () => unsub();
  }, [today]);

  const flyTo = (t) => {
    setSelected(t);
    mapInstance.current?.flyTo([t.lat, t.lng], 13, { duration: 1.2 });
    markersRef.current[t.trainId]?.openPopup();
  };

  const filteredList = Object.values(trains).filter(t =>
    !search || String(t.trainId).includes(search) || t.staffName?.toLowerCase().includes(search.toLowerCase())
  );

  const timeAgo = (ts) => {
    const d = Date.now() - ts;
    if (d < 60000) return `${Math.floor(d/1000)}s আগে`;
    return `${Math.floor(d/60000)}m আগে`;
  };

  return (
    <div>
      <PageHeader
        title="🚂 Train Tracker"
        sub={`${Object.keys(trains).length} টি train এখন live`}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, height: 'calc(100vh - 160px)' }}>
        {/* Left panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Train ID বা Staff নাম..." />

          <div style={{
            background: '#232323', border: '1px solid #3a3a3a', borderRadius: 6,
            flex: 1, overflow: 'auto',
          }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#646970' }}>লোড হচ্ছে...</div>
            ) : filteredList.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#646970' }}>কোনো live train নেই</div>
            ) : filteredList.map(t => (
              <div key={t.trainId} onClick={() => flyTo(t)} style={{
                padding: '12px 16px', borderBottom: '1px solid #2a2a2a', cursor: 'pointer',
                background: selected?.trainId === t.trainId ? '#1a2a3a' : 'transparent',
                borderLeft: selected?.trainId === t.trainId ? '3px solid #2271b1' : '3px solid transparent',
                transition: 'all .15s',
              }}
              onMouseEnter={e => { if (selected?.trainId !== t.trainId) e.currentTarget.style.background = '#2a2a2a'; }}
              onMouseLeave={e => { if (selected?.trainId !== t.trainId) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#f0f0f1', fontWeight: 600, fontSize: 13 }}>Train #{t.trainId}</span>
                  <span style={{
                    background: t.speed > 10 ? 'rgba(0,163,42,.2)' : 'rgba(219,166,23,.2)',
                    color: t.speed > 10 ? '#00a32a' : '#dba617',
                    border: `1px solid ${t.speed > 10 ? 'rgba(0,163,42,.4)' : 'rgba(219,166,23,.4)'}`,
                    borderRadius: 3, padding: '1px 6px', fontSize: 11, fontWeight: 600, fontFamily: 'DM Mono',
                  }}>{t.speed.toFixed(1)} km/h</span>
                </div>
                <div style={{ color: '#646970', fontSize: 11, marginTop: 4 }}>
                  👤 {t.staffCount} staff · 🕐 {timeAgo(t.timestamp)}
                </div>
                <div style={{ color: '#a7aaad', fontSize: 11, marginTop: 2, fontFamily: 'DM Mono' }}>
                  {t.lat.toFixed(4)}, {t.lng.toFixed(4)}
                </div>
              </div>
            ))}
          </div>

          {/* Selected detail */}
          {selected && (
            <div style={{ background: '#232323', border: '1px solid #2271b1', borderRadius: 6, padding: 16 }}>
              <div style={{ color: '#f0f0f1', fontWeight: 700, marginBottom: 10 }}>Train #{selected.trainId}</div>
              {[
                ['🚀 Speed', `${selected.speed.toFixed(1)} km/h`],
                ['👤 Staff', `${selected.staffCount} জন`],
                ['📍 Lat', selected.lat.toFixed(6)],
                ['📍 Lng', selected.lng.toFixed(6)],
                ['🕐 Update', timeAgo(selected.timestamp)],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #2a2a2a' }}>
                  <span style={{ color: '#a7aaad', fontSize: 12 }}>{l}</span>
                  <span style={{ color: '#f0f0f1', fontSize: 12, fontFamily: 'DM Mono' }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div style={{ border: '1px solid #3a3a3a', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(26,26,26,.8)', color: '#a7aaad' }}>
              Map লোড হচ্ছে...
            </div>
          )}
          {/* Legend */}
          <div style={{ position: 'absolute', bottom: 20, right: 10, background: 'rgba(35,35,35,.95)', border: '1px solid #3a3a3a', borderRadius: 6, padding: '10px 14px', zIndex: 1000 }}>
            <div style={{ color: '#a7aaad', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>LEGEND</div>
            {[['#00a32a','চলছে (>10 km/h)'],['#dba617','ধীরে (<10 km/h)']].map(([c,l]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: c, boxShadow: `0 0 4px ${c}` }}></div>
                <span style={{ color: '#a7aaad', fontSize: 11 }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
